import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';
import { useEditorStore, EditItem } from '../../stores/editorStore';
import ErrorTooltip from './ErrorTooltip';
import BezierOverlay from './BezierOverlay';

interface TextEditorProps {
  readOnly?: boolean;
}

const TextEditor: React.FC<TextEditorProps> = ({ readOnly = false }) => {
  const {
    originalText,
    edits,
    appliedEdits,
    activeEditIndex,
    hoveredEditIndex,
    setOriginalText,
    setActiveEdit,
    setHoveredEdit,
    setTooltipPosition,
    applyEdit,
    applyGroupEdits,
  } = useEditorStore();

  const editorRef = useRef<HTMLDivElement>(null);

  // Track whether we're programmatically updating the editor to avoid feedback loops
  const isUpdatingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      History,
      Placeholder.configure({
        placeholder: 'Enter or paste your text here...',
      }),
    ],
    content: originalText,
    editable: !readOnly,
    editorProps: {
      attributes: {
        spellcheck: 'false',
        autocorrect: 'off',
        autocapitalize: 'off',
      },
    },
    onUpdate: ({ editor }) => {
      // Only update store from user typing, not from programmatic updates
      // Also skip when edits are present (user shouldn't be typing then)
      if (!readOnly && !isUpdatingRef.current && edits.length === 0) {
        setOriginalText(editor.getText());
      }
    },
  });

  // Update editor content when original text changes (only when no edits present)
  useEffect(() => {
    // Only sync editor when no edits are present - prevents feedback loops
    if (editor && edits.length === 0 && editor.getText() !== originalText) {
      isUpdatingRef.current = true;
      editor.commands.setContent(originalText);
      isUpdatingRef.current = false;
    }
  }, [originalText, editor, edits.length]);

  // Handle right-click context menu - apply only the clicked edit (and connected edits)
  const handleContextMenu = (e: React.MouseEvent, editIndex: number) => {
    e.preventDefault();
    const edit = edits[editIndex];
    console.log('[handleContextMenu] Called with editIndex:', editIndex);
    console.log('[handleContextMenu] Edit:', edit);
    console.log('[handleContextMenu] All edits:', edits);
    console.log('[handleContextMenu] Applied edits:', Array.from(appliedEdits));
    if (!edit) return;

    // If this edit has a group_id (not null/undefined), apply all edits in that group
    // (for connected edits like MOVE + INSERT that should be applied together)
    // Note: API returns null for no group, so check for both null and undefined
    if (edit.group_id != null) {
      console.log('[handleContextMenu] Edit has group_id:', edit.group_id, '- applying group edits');
      applyGroupEdits(edit.group_id);
    } else {
      // Otherwise, just apply this single edit
      console.log('[handleContextMenu] No group_id - applying single edit');
      applyEdit(editIndex);
    }
  };

  // Get highlighted content with error markers
  const getHighlightedContent = () => {
    if (!edits.length || !originalText) return originalText;

    // Collect all marker positions: both edit spans and MOVE destinations
    interface MarkerPoint {
      position: number;
      type: 'edit-start' | 'edit-end' | 'move-dest';
      editIndex: number;
      edit: EditItem;
    }

    const markerPoints: MarkerPoint[] = [];

    edits.forEach((edit, index) => {
      if (!appliedEdits.has(index)) {
        // Add start and end points for the edit
        markerPoints.push({
          position: edit.from_span[0],
          type: 'edit-start',
          editIndex: index,
          edit,
        });
        markerPoints.push({
          position: edit.from_span[1],
          type: 'edit-end',
          editIndex: index,
          edit,
        });

        // For MOVE edits, also add destination marker
        if (edit.type === 'MOVE' && edit.to_span) {
          markerPoints.push({
            position: edit.to_span[0],
            type: 'move-dest',
            editIndex: index,
            edit,
          });
        }
      }
    });

    // Sort by position (ascending)
    markerPoints.sort((a, b) => a.position - b.position);

    // Build content with markers
    const content = originalText;
    const elements: React.ReactNode[] = [];
    let currentPos = 0;
    const activeEdits = new Set<number>(); // Track which edits we're inside

    markerPoints.forEach((point, pointIndex) => {
      // Add text before this marker
      if (point.position > currentPos) {
        const textBetween = content.slice(currentPos, point.position);
        if (textBetween) {
          // Check if we're inside an edit span
          if (activeEdits.size > 0) {
            const editIndex = Array.from(activeEdits)[0];
            const edit = edits[editIndex];
            const isActive = activeEditIndex === editIndex;
            const isHovered = hoveredEditIndex === editIndex;
            const className = `error-highlight ${isActive || isHovered ? 'bg-red-200 dark:bg-red-800/50' : ''}`;

            if (edit.type === 'MOVE') {
              elements.push(
                <span
                  key={`move-text-${editIndex}-${pointIndex}`}
                  className={`move-indicator ${className}`}
                  data-edit-index={editIndex}
                  data-move-type="source"
                  onMouseEnter={(e) => {
                    setHoveredEdit(editIndex);
                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    setTooltipPosition({ x: rect.left, y: rect.bottom + 5 });
                  }}
                  onMouseLeave={() => setHoveredEdit(null)}
                  onClick={() => setActiveEdit(activeEditIndex === editIndex ? null : editIndex)}
                  onContextMenu={(e) => handleContextMenu(e, editIndex)}
                >
                  {textBetween}
                </span>
              );
            } else {
              elements.push(
                <span
                  key={`edit-text-${editIndex}-${pointIndex}`}
                  className={className}
                  data-edit-index={editIndex}
                  onMouseEnter={(e) => {
                    setHoveredEdit(editIndex);
                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    setTooltipPosition({ x: rect.left, y: rect.bottom + 5 });
                  }}
                  onMouseLeave={() => setHoveredEdit(null)}
                  onClick={() => setActiveEdit(activeEditIndex === editIndex ? null : editIndex)}
                  onContextMenu={(e) => handleContextMenu(e, editIndex)}
                >
                  {textBetween}
                </span>
              );
            }
          } else {
            elements.push(textBetween);
          }
        }
        currentPos = point.position;
      }

      // Handle the marker point
      if (point.type === 'edit-start') {
        activeEdits.add(point.editIndex);

        // For INSERT, add the insert marker
        if (point.edit.type === 'INSERT') {
          elements.push(
            <span
              key={`insert-${point.editIndex}`}
              className="insert-marker"
              data-edit-index={point.editIndex}
              onMouseEnter={(e) => {
                setHoveredEdit(point.editIndex);
                const rect = (e.target as HTMLElement).getBoundingClientRect();
                setTooltipPosition({ x: rect.left, y: rect.bottom + 5 });
              }}
              onMouseLeave={() => setHoveredEdit(null)}
              onClick={() => setActiveEdit(activeEditIndex === point.editIndex ? null : point.editIndex)}
              onContextMenu={(e) => handleContextMenu(e, point.editIndex)}
            />
          );
        }
      } else if (point.type === 'edit-end') {
        activeEdits.delete(point.editIndex);
      } else if (point.type === 'move-dest') {
        // Add destination marker for MOVE (triangle pointing down)
        const isActive = activeEditIndex === point.editIndex;
        const isHovered = hoveredEditIndex === point.editIndex;
        elements.push(
          <span
            key={`move-dest-${point.editIndex}`}
            className={`move-destination ${isActive || isHovered ? 'active' : ''}`}
            data-edit-index={point.editIndex}
            data-move-type="destination"
            onMouseEnter={(e) => {
              setHoveredEdit(point.editIndex);
              const rect = (e.target as HTMLElement).getBoundingClientRect();
              setTooltipPosition({ x: rect.left, y: rect.bottom + 5 });
            }}
            onMouseLeave={() => setHoveredEdit(null)}
            onClick={() => setActiveEdit(activeEditIndex === point.editIndex ? null : point.editIndex)}
          >
            ▼
          </span>
        );
      }
    });

    // Add remaining text after all markers
    if (currentPos < content.length) {
      elements.push(content.slice(currentPos));
    }

    // Handle applied edits (show corrected text)
    // This is simplified - in a full implementation you'd need to track applied edits too

    return elements;
  };

  // Get move edits for Bezier curves
  const moveEdits = edits
    .map((edit, index) => ({ edit, index }))
    .filter(({ edit, index }) => edit.type === 'MOVE' && !appliedEdits.has(index));

  return (
    <div className="relative" ref={editorRef}>
      {/* Main editor (when no edits) */}
      {edits.length === 0 && (
        <div className="prose dark:prose-invert max-w-none">
          <EditorContent editor={editor} className="min-h-[300px]" />
        </div>
      )}

      {/* Highlighted view (when edits present) */}
      {edits.length > 0 && (
        <div className="min-h-[300px] p-4 whitespace-pre-wrap font-sans text-base leading-relaxed">
          {getHighlightedContent()}
        </div>
      )}

      {/* Bezier curve overlay for moves */}
      {moveEdits.length > 0 && editorRef.current && (
        <BezierOverlay
          moves={moveEdits.map(({ edit, index }) => ({
            fromSpan: edit.from_span,
            toSpan: edit.to_span || [0, 0],
            editIndex: index,
          }))}
          containerRef={editorRef}
        />
      )}

      {/* Error tooltip */}
      <ErrorTooltip />
    </div>
  );
};

export default TextEditor;
